import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import ReactSelect from 'react-select';
import { FormSelectInput, SelectStyles, selectPortalProps } from '~/components/forms/Select/Select';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_PARSE_HINTS,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_PARSE_HINTS,
} from './constants';
import { withParseHint } from './parseHintLabel';

export type FileTypeOption = { label: string; value: string };

interface RowSelectProps {
  // Names the control for assistive tech, e.g. `File Type for cv.pdf`.
  ariaLabel: string;
  value: FileTypeOption | null;
  disabled?: boolean;
  invalid?: boolean;
  cyTag?: string;
  errorId?: string;
  // The id of a note about this file (MCV-1802), read with the control's label.
  describedById?: string;
}

interface Props extends RowSelectProps {
  onChange: (option: FileTypeOption) => void;
}

interface CategoryProps extends RowSelectProps {
  // null: the category was cleared (it is optional).
  onChange: (option: FileTypeOption | null) => void;
}

interface RowSelectBaseProps extends RowSelectProps {
  options: readonly FileTypeOption[];
  hints: Record<string, string>;
  placeholder: string;
  clearable: boolean;
  onChange: (option: FileTypeOption | null) => void;
}

const RowSelect = ({
  ariaLabel,
  value,
  onChange,
  disabled,
  invalid,
  cyTag,
  errorId,
  describedById,
  options,
  hints,
  placeholder,
  clearable,
}: RowSelectBaseProps) => (
  <div data-cy={cyTag} className="w-full sm:w-48">
    <ReactSelect<FileTypeOption, false>
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      aria-errormessage={invalid ? errorId : undefined}
      aria-describedby={describedById}
      value={value?.label ? value : null}
      unstyled
      isDisabled={disabled}
      isClearable={clearable}
      options={options}
      // [MCV-1810] The parse hints sit in the open menu only (the selected value stays plain), on a
      // second line so they fit this narrow control.
      formatOptionLabel={withParseHint(hints, 'stacked')}
      classNames={SelectStyles(false, invalid)}
      onChange={(option) => onChange(option)}
      menuPortalTarget={selectPortalProps.menuPortalTarget}
      menuPosition={selectPortalProps.menuPosition}
      styles={{
        container: (base) => ({ ...base, width: '100%', maxWidth: '100%' }),
        singleValue: (base) => ({
          ...base,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }),
        menuPortal: selectPortalProps.menuPortalStyle,
      }}
      placeholder={<div className="body-text text-grey-700">{placeholder}</div>}
      components={{
        Input: FormSelectInput,
        DropdownIndicator: (props) =>
          props.selectProps.menuIsOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-grey-100" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-grey-100" />
          ),
      }}
    />
  </div>
);

/**
 * [MCV-1765] The File Type of one file in the Add Document list. The form's own Select is bound
 * to a react-hook-form field by name; each file of a batch keeps its type in the form's local
 * state instead, so this is the same control (and look) without that binding.
 * [MCV-1810] It is the only File Type control of the Add Document form, for one file as for
 * several.
 */
export const FileTypeSelect = ({ onChange, ...rest }: Props) => (
  <RowSelect
    {...rest}
    options={DOCUMENT_TYPES}
    hints={DOCUMENT_TYPE_PARSE_HINTS}
    placeholder="File Type"
    clearable={false}
    onChange={(option) => {
      if (option) onChange(option);
    }}
  />
);

/**
 * [MCV-1848] The File Category of one file in the Add Document list, to the right of its File
 * Type. Same control and look; the category is optional, so it can be cleared.
 */
export const FileCategorySelect = (props: CategoryProps) => (
  <RowSelect
    {...props}
    options={DOCUMENT_CATEGORIES}
    hints={DOCUMENT_CATEGORY_PARSE_HINTS}
    placeholder="Category (optional)"
    clearable
  />
);
